/** @jsx jsx */
import React, { useEffect, useRef } from 'react';
import ReactTooltip from 'react-tooltip';

import { FormFieldErrorMessage } from './FormFieldErrorMessage';
import styled from '@emotion/styled';
import { FormFieldLabel } from './FormFieldLabel';

// import './styles.scss';
import { InputCheckbox } from '../InputCheckbox';
import { jsx, css, SerializedStyles } from '@emotion/react';

import { media } from '../../services/media';
import { parseHTML } from '../../services/parseHTML';
import { classNames } from '../../services/classNames';

interface FormFieldProps {
    name: string;
    inputType: string;
    label?: string;
    value?: string;
    placeholder?: string;
    pattern?: string;
    className?: string;
    error: boolean;
    errorMsg?: string;
    onChange: () => void;
    inputMode: any;
    maxlength?: number;
    required: boolean;
    disabled: boolean;
    ruleLabel?: string;
    dataEvent?: string;
    variant: 'boxedBahamaBlue';
    checked?: boolean;
}

const InputVariants: Record<any, SerializedStyles> = {
    boxedBahamaBlue: css``,
};

export const FormField: React.FC<FormFieldProps> = ({
    name,
    inputType = 'text',
    label = '',
    value = '',
    placeholder = '',
    pattern = '',
    className = '',
    error = false,
    errorMsg = '',
    onChange,
    inputMode,
    maxlength = undefined,
    required = false,
    disabled = false,
    ruleLabel = '',
    dataEvent = '',
    variant = '',
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const max =
        inputType === 'number' && maxlength
            ? Math.pow(10, maxlength) - 1
            : undefined;

    useEffect(() => {
        if (error) {
            const tooltip = document.querySelectorAll(
                `[data-tip][data-for="errorMessage-${name}"]`,
            )[0];
            ReactTooltip.show(tooltip);
        }
    }, [error]);

    return name ? (
        <FormFieldLabel
            htmlFor={name}
            className={classNames(
                'FormField__label',
                `FormField__label--${inputType}`,
                className && `${className}__label`,
                error ? 'error' : '',
            )}
        >
            <Input
                type={inputType}
                name={name}
                id={name}
                value={value}
                autoComplete="off"
                onChange={onChange}
                required={required}
                disabled={disabled}
                className={classNames(
                    'FormField__input',
                    className && `${className}__input`,
                )}
                placeholder={placeholder}
                pattern={pattern || undefined}
                maxLength={maxlength || undefined}
                max={max}
                inputMode={inputMode || undefined}
                data-tip
                data-for={`errorMessage-${name}`}
                data-event={dataEvent || 'false'}
                ref={inputRef}
                css={InputVariants[variant]}
            />
            {error && errorMsg && (
                <Tooltip
                    type="error"
                    clickable
                    id={`errorMessage-${name}`}
                    effect="solid"
                    className="FormField__error-tooltip"
                >
                    <FormFieldErrorMessage>
                        {parseHTML(errorMsg, 'html')}
                    </FormFieldErrorMessage>
                </Tooltip>
            )}

            <Label>
                {inputType === 'checkbox' ? (
                    <LabelText>
                        <InputCheckbox
                            checked={inputRef.current?.checked || false}
                        />
                        {parseHTML(label, 'html')}{' '}
                    </LabelText>
                ) : (
                    <Label>
                        {parseHTML(label, 'html')}{' '}
                        {!error && !errorMsg && ruleLabel && (
                            <div>{ruleLabel}</div>
                        )}
                    </Label>
                )}
            </Label>
        </FormFieldLabel>
    ) : null;
};

const LabelText = styled.div`
    display: grid;
    text-align: left;
    align-items: center;

    ${media.max('md')} {
        grid-template-columns: 20px auto;
        gap: 10px;
    }

    ${media.min('md')} {
        gap: 15px;
        grid-template-columns: 30px auto;
    }

    a {
        margin: 0 1ex;
    }
`;

const Label = styled.div``;

export const Input = styled.input`
    width: 100%;
    padding: 2vw;
    margin-bottom: 0.48em;
    font-size: 4.6vw;
    text-align: center;
    color: white;
    border-bottom: 3px solid;

    font-family: ${(props) => props.theme.headings.fontFamily};

    ${media.min('md')} {
        padding: 1.5vw;

        font-size: 4vw;

        border-bottom-width: 5px;
    }

    ${media.min('xl')} {
        padding: 1vw 1vw;

        font-size: 2vw;

        border-bottom-width: 8px;
    }

    &[type='checkbox'] {
        position: absolute;
        opacity: 0;
        left: 0;
        top: 0;
        height: 100%;
        cursor: pointer;
        margin: 0;
    }

    &:disabled {
        color: rgba(255, 255, 255, 0.6);
        cursor: not-allowed;
        border-color: white;
    }

    &::-webkit-input-placeholder {
        color: rgba(255, 255, 255, 0.6);
    }

    &:-ms-input-placeholder {
        color: rgba(255, 255, 255, 0.6);
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.6);
    }
    &::-webkit-credentials-auto-fill-button,
    &::-webkit-contacts-auto-fill-button {
        display: none;

        visibility: hidden;
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        margin: 0;

        -webkit-appearance: none;
    }
`;

const Tooltip = styled(ReactTooltip)`
    opacity: 0.9 !important;
    margin-top: -1px !important;
    visibility: visible !important;
    max-width: 320px;

    ${media.min('sm')} {
        max-width: 400px;
    }
`;
